import { useEffect, useRef, useState } from 'react';
import { isCorrectFeedback } from '../feedbackAudio';
import { playRoundClip } from './roundClipPlayback';

// Uses the same accepted-answer and resumable audio queue contract as the earlier tours.
export default function useWordAdventure({ round, sharedAudioRef, localProgress, onComplete }) {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('waiting');
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [completed, setCompleted] = useState(0);
  const [error, setError] = useState('');
  const [wrong, setWrong] = useState(false);
  const ownAudio = useRef(null);
  const audioRef = sharedAudioRef || ownAudio;
  const handlers = useRef({});
  const completion = useRef(onComplete);
  completion.current = onComplete;
  useEffect(() => {
    let alive = true, running = false, locked = true, cursor = 0, retry, cancelAudio, cancelDelay, finished = false;
    let events = [], chosen = null, placed = [], helpShown = false;
    const imageCancels = new Set(), loadedImages = new Set();
    const bridge = round.assessment_mode === 'syllable_bridge';
    const chest = round.assessment_mode === 'silent_word_chest';
    const target = () => chest ? [] : [round.items[cursor].audio_id];
    const report = () => localProgress(round.game_id, events);
    const playOnce = (id) => {
      if (!alive) return Promise.reject(new Error('cancelled'));
      const playback = playRoundClip(round, audioRef, id);
      cancelAudio = playback.cancel;
      return playback.done.finally(() => { if (cancelAudio === playback.cancel) cancelAudio = null; });
    };
    const play = (id) => playOnce(id).catch((err) => {
      if (!alive) throw err;
      if (isCorrectFeedback(id)) return;
      return playOnce(id);
    });
    const speech = (ids) => {
      let position = 0;
      return async () => { while (alive && position < ids.length) { await play(ids[position]); position += 1; } };
    };
    const loadPicture = (url) => new Promise((resolve, reject) => {
      if (loadedImages.has(url)) { resolve(); return; }
      const image = new Image();
      let timer, settled = false;
      const clean = () => { clearTimeout(timer); image.onload = null; image.onerror = null; imageCancels.delete(cancel); };
      const cancel = () => { if (!settled) { settled = true; clean(); reject(new Error('Görsel yüklenemedi. Tekrar deneyelim.')); } };
      imageCancels.add(cancel);
      image.onload = () => { if (!settled) { settled = true; clean(); loadedImages.add(url); resolve(); } };
      image.onerror = cancel;
      timer = setTimeout(cancel, 30000);
      image.src = url;
    });
    const prepare = () => Promise.all((round.items[cursor].choices || []).map((choice) =>
      loadPicture(choice.image.url).catch((err) => { if (!alive) throw err; return loadPicture(choice.image.url); })));
    const delay = () => new Promise((resolve) => {
      const timer = setTimeout(() => { cancelDelay = null; resolve(); }, 650);
      cancelDelay = () => { clearTimeout(timer); resolve(); };
    });
    const ready = () => { if (alive) { locked = false; setPhase('ready'); } };
    const run = async (operation) => {
      if (!alive || running) return;
      locked = true; running = true; setPhase('playing'); setError('');
      retry = () => run(operation);
      try { await operation(); }
      catch (err) { if (alive) { setError(err.name === 'NotAllowedError' ? 'Ses başlayamadı. Tekrar Dene düğmesine dokun.' : err.message); setPhase('retry'); } }
      finally { running = false; }
    };
    const intro = speech([round.intro_audio_id, ...target()]);
    const start = () => run(async () => { await prepare(); await intro(); if (alive) { report(); ready(); } });
    const replay = (instruction = false) => {
      if (locked) return;
      const queue = speech([...(instruction ? [round.intro_audio_id] : []), ...target()]);
      run(async () => { await queue(); ready(); });
    };
    const help = () => {
      if (locked || helpShown) return;
      helpShown = true;
      const queue = speech([round.help_audio_id]);
      run(async () => { await queue(); ready(); });
    };
    const accept = (ids) => {
      const item = round.items[cursor];
      const text = chest ? ids[0] : ids.map((id) => item.stones.find((stone) => stone.id === id).text).join('');
      const answer = round.assessment_mode === 'word_repair' ? item.prefix + text + item.suffix : text;
      const correct = answer === item.expected_id;
      events = [...events, { kind: 'select', item_id: item.id, selected_id: ids.join(',') }];
      const result = report();
      const queue = speech(correct ? ['s_l1_g2_pop', round.correct_audio_ids[cursor % round.correct_audio_ids.length]]
        : [round.wrong_audio_id, ...target()]);
      const finishSpeech = speech([round.complete_audio_id]);
      const nextSpeech = speech(!chest && cursor + 1 < round.items.length ? [round.items[cursor + 1].audio_id] : []);
      let advanced = false, animated = false;
      chosen = null; setSelected(null); setWrong(!correct);
      if (!correct) { placed = []; setSlots([]); }
      run(async () => {
        await queue();
        if (!alive) return;
        if (!correct) { ready(); return; }
        setPhase('finishing'); setCompleted(cursor + 1);
        if (!animated) { await delay(); animated = true; }
        if (!alive) return;
        if (result.round_completed) {
          await finishSpeech();
          if (alive && !finished) { finished = true; completion.current(result.summary); }
          return;
        }
        if (!advanced) { cursor += 1; advanced = true; placed = []; setSlots([]); setIndex(cursor); setWrong(false); }
        await prepare(); await nextSpeech(); ready();
      });
    };
    const place = (id, position = 0) => {
      if (locked) return;
      const item = round.items[cursor];
      if (!item.stones?.some((stone) => stone.id === id) || placed.includes(id)) return;
      const count = bridge ? 2 : 1;
      if (position < 0 || position >= count) return;
      placed = Array.from({ length: count }, (_, i) => i === position ? id : placed[i] || null);
      setSlots(placed); chosen = null; setSelected(null); setWrong(false);
      if (placed.every(Boolean)) accept(placed);
    };
    const choose = (id) => {
      if (locked) return;
      if (chest) {
        if (round.items[cursor].choices.some((choice) => choice.id === id)) accept([id]);
      } else if (bridge) { chosen = chosen === id ? null : id; setSelected(chosen); }
      else place(id);
    };
    const slot = (position) => {
      if (locked) return;
      if (chosen) place(chosen, position);
      else { placed = placed.map((value, i) => i === position ? null : value); setSlots(placed); setWrong(false); }
    };
    handlers.current = { start, replay, help, choose, place, slot, retry: () => retry?.() };
    if (round.continuing) start();
    return () => {
      alive = false;
      locked = true;
      // playRoundClip.cancel already pauses the currently playing Audio instance.
      // Do not read audioRef.current during effect cleanup: it may have changed.
      cancelAudio?.();
      cancelDelay?.();
      [...imageCancels].forEach((cancel) => cancel());
    };
  }, [round, audioRef, localProgress]);
  return { index, phase, slots, selected, completed, error, wrong, handlers };
}
